import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ErroDaApi, api, type TemaDaArea } from '../lib/api.js';
import { slugDaAreaNoCaminho } from '../lib/endereco.js';
import { temaSemeado } from '../lib/semente.js';
import { variaveisDoTema } from '../lib/tema.js';
/* `import type`: apagado na compilação, e por isso não puxa a folha para o
   pedaço de entrada. O VALOR chega pelo `lazy()` lá em baixo. */
import type { TermosPublicados } from './FolhaDosTermos.js';
import { telaComDicionario } from '../lib/tela-com-dicionario.js';
import { useT } from '../lib/idioma.js';

/**
 * O portão dos termos de uso — a tela que o aluno vê antes de qualquer coisa.
 *
 * ## O que esta tela é, e o que ela NÃO é
 *
 * Ela **não** é a tranca. A tranca é o servidor: `exigirMatricula`
 * (`apps/api/src/lib/escopo-aluno.ts`) recusa com 403 `termos_pendentes` toda
 * rota `/a/:areaSlug/...` de quem não aceitou — a vitrine, a aula, o player que
 * assina a URL do vídeo, a agenda, o mural. Apagar este componente inteiro não
 * abre nada; só troca uma tela explicativa por uma sequência de erros.
 *
 * Esta tela existe para que a pessoa **saiba o que fazer** — e, desde
 * 24/09/2026, para que ela consiga **ler** o que vai aceitar.
 *
 * ## ── A LEVA DE 24/09/2026: o documento, e não o aviso de sistema ─────────
 *
 * Ordem do dono, com captura na mão, depois de publicar os termos de verdade
 * (7.927 caracteres) e entrar como aluno:
 *
 * > *«use os agentes uiux e design para melhorar o design desses termos, ele tá
 * > pequeno, não funcional, quebra as regras do juiz e faça algo elegante e
 * > premium»*
 *
 * O que estava no ar, medido na bancada (`ver-o-portao.ts`, cena `paragrafos`,
 * tema laranja) contra o componente de antes:
 *
 * | o que se mediu | antes | agora |
 * | --- | --- | --- |
 * | superfícies que rolam @390 | **2** (a página e a janelinha) | 1 |
 * | coluna de texto @1440 | 592 px, **79** caracteres por linha | 578 px, **68** |
 * | corpo do texto | 15 px sem serifa | 17 px com serifa, entrelinha 29 |
 * | hierarquia no corpo legal | 0 secções, 0 cláusulas marcadas | 9 e 31 |
 * | o fim do documento | não existia sinal nenhum | anunciado, e contado |
 * | cor do gesto @tema laranja | `rgb(34,197,94)` — o **verde da fábrica** | `rgb(249,115,22)`, o do tutor |
 *
 * As quatro decisões de desenho, e o que cada uma responde:
 *
 * **1. A página rola, e não uma caixa dentro dela.** O texto morava num
 * `max-h-[55vh] overflow-y-auto`: no telemóvel eram duas barras verticais em
 * eixos concorrentes, e em qualquer largura o documento acabava cortado a meio
 * de uma frase — *«2.3. É proibido compartilhar credenciais…»* — sem nada que
 * dissesse quanto faltava. O argumento que a caixa tinha (*«o botão está sempre
 * onde ela o deixou»*) continua de pé e passou para a **barra colada em baixo**,
 * que é `sticky` e não rouba um eixo de rolagem.
 *
 * **2. A tela larga vira orientação, e nunca linha mais comprida.** De `lg` para
 * cima há duas colunas: o trilho (identificação, versão, data, tempo de leitura
 * e o **índice das cláusulas** do próprio documento) e a folha. A coluna de
 * leitura fica em 68 caracteres, dentro da medida que os tipógrafos pedem; o que
 * a tela grande ganha é o mapa ao lado, e não uma linha mais comprida.
 *
 * **3. O ritmo sai da numeração que o documento já tem.** Nenhuma palavra é
 * alterada, resumida ou destacada — o aceite é sobre o texto que a pessoa leu, e
 * o HTML vem congelado do servidor. O que esta tela faz é **ler a hierarquia que
 * já está escrita**: `1. Objeto` numa linha curta é um título de secção;
 * `2.3. …` é uma cláusula, e ganha o recuo pendente que põe o número na margem.
 * Ver `marcarODocumento`, e o bloco `.vst-termos` de `index.css`.
 *
 * **4. O gesto acompanha a leitura.** A barra de baixo tem dois estados: enquanto
 * falta documento, ela diz quanto já se leu e oferece «Continuar a leitura»;
 * chegado o fim, ela vira a declaração e o botão. Não há botão apagado à espera:
 * um controlo desabilitado que não diz por quê foi o que o dono viu.
 *
 * ## Onde mora a doutrina do HTML congelado
 *
 * **Em `FolhaDosTermos.tsx`, com a injeção.** A razão por que o corpo dos termos
 * vem pronto do servidor, e por que o `dangerouslySetInnerHTML` é seguro pelo
 * MECANISMO e nunca pela origem, viveu aqui até 24/09/2026 — e mudou-se junto
 * com a injeção quando a folha virou pedaço próprio. Uma justificação que fica
 * onde o código já não está é uma doutrina morta a ensinar por inércia, e é
 * exactamente o defeito que aquele texto descreve.
 */

interface RespostaDosTermos {
  termos: TermosPublicados | null;
  portao: { liberado: boolean; motivo?: 'nunca_aceitou' | 'texto_mudou'; versaoExigida?: number };
  /**
   * A paleta do TUTOR — acrescentada à rota em 24/09/2026, e o conserto de uma
   * cor errada que ninguém tinha nomeado.
   *
   * O portão vive **fora das rotas** (ver abaixo), e portanto fora do elemento
   * que veste `variaveisDoTema()`. Sem tema nenhum, as variáveis não resolviam e
   * cada `var(--destaque, #22c55e)` caía na reserva: a primeira tela depois da
   * compra usava o **verde de fábrica** numa área laranja. Medido em produção
   * pelo dono, e reproduzido na bancada.
   *
   * Opcional de propósito: um servidor mais antigo não manda o campo, e nesse
   * caso a semente do documento (`temaSemeado`) responde — e, faltando as duas,
   * volta a ser o tema de fábrica, como era.
   */
  tema?: TemaDaArea;
}

/*
 * A régua de «este caminho é de área?» mora em `lib/endereco.ts` desde a `CE4`,
 * e não aqui. Ela estava escrita à mão neste ficheiro e **não funcionava no
 * domínio próprio de um cliente**, onde o caminho não tem `/a/<slug>` — o
 * portão simplesmente nunca corria ali. Endereçar é o assunto daquele ficheiro,
 * e é lá que quem acrescentar uma rota vai tropeçar nela.
 */

export function PortaoDosTermos() {
  const t = useT();
  const local = useLocation();
  const slug = slugDaAreaNoCaminho(local.pathname);

  const [dados, setDados] = useState<RespostaDosTermos | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [marcado, setMarcado] = useState(false);
  /* A fase 2 do wizard — se o aceite já passou. Quem decide se há alguma coisa
     a cadastrar é a FOLHA, que já tem a régua; ver `aceitar`, abaixo. */
  const [fase2, setFase2] = useState(false);

  const buscar = useCallback(async (alvo: string) => {
    try {
      setDados(await api.get<RespostaDosTermos>(`/a/${encodeURIComponent(alvo)}/termos`));
    } catch {
      /*
       * Silêncio de propósito. Quem não tem sessão, não tem matrícula ou pediu
       * uma área que não existe já vai ser mandado ao lugar certo pela tela de
       * baixo, que carrega em paralelo — `useCarregar` trata 401 e 404 com as
       * regras dela. Um segundo erro desenhado por cima seria dois diagnósticos
       * para o mesmo problema, e o de cima é o que não sabe de nada.
       */
      setDados(null);
    }
  }, []);

  useEffect(() => {
    if (!slug) {
      setDados(null);
      return;
    }
    void buscar(slug);
    /* O estado do aceite é por ÁREA, e não por tela: só refaz o pedido quando a
       pessoa troca de área. Sem isto, navegar entre aulas repetiria a consulta a
       cada clique de quem já aceitou — que é toda a base menos os que chegam. */
  }, [slug, buscar]);

  if (!slug || !dados?.termos || dados.portao.liberado) return null;

  const { termos } = dados;
  const primeiraVez = dados.portao.motivo === 'nunca_aceitou';

  async function aceitar() {
    if (!slug) return;
    setEnviando(true);
    setErro(null);
    try {
      await api.post(`/a/${encodeURIComponent(slug)}/termos/aceite`, { termosId: termos.id });

      /*
       * ── A FASE 2 DO WIZARD, E POR QUE ELA VEM ANTES DO `reload` ───────────
       *
       * Ordem do dono de 06/10/2026: *«logo após a assinatura do termo, o
       * usuário seja levado para uma segunda fase do wizard para cadastrar de
       * forma opcional o perfil»*.
       *
       * **Antes do `reload`, e nunca depois.** O convite já aparecia depois de
       * recarregar — `PortaoDoInstagram` monta-o assim que os termos deixam de
       * estar pendentes —, e isso não é um wizard: é a página a piscar, a área a
       * desenhar por trás, e uma carta a subir do rodapé sem ligação nenhuma com
       * o que a pessoa acabou de fazer. Mantendo-a aqui, o aceite leva à fase 2
       * na MESMA cortina, e o salto só acontece no fim das duas.
       *
       * E o `reload` fica inteiro, com a razão dele intacta: as telas por baixo
       * pediram os dados delas e levaram 403, não há caminho de «tente de novo»
       * que passe por todas, e recarregar é o gesto que a pessoa faria de
       * qualquer forma. Ele só mudou de instante — passa a correr quando a fase
       * 2 fecha (`aoFechar`, abaixo), em vez de aqui.
       *
       * Quem já declarou o `@`, ou já esgotou os cinco convites, não tem fase 2:
       * para essa pessoa esta linha é o `reload` de sempre, e o `catch` garante
       * que uma leitura estranha de `/eu` nunca a prende numa área que ela já
       * aceitou.
       */
      /*
       * ── A PORTA NÃO DECIDE, E ISSO SÃO 427 BYTES MEDIDOS ─────────────────
       *
       * A primeira versão desta leva pedia `/eu` aqui e perguntava ao core se
       * ainda faltava convite. Funcionava, e **punha
       * `area/convite-do-instagram.ts` no pedaço de entrada**: medido a trocar o
       * import por um inline e a reconstruir, **427 B** na porta de toda tela da
       * área, para servir uma decisão que acontece uma vez na vida.
       *
       * É a lição que esta casa já escreveu em `lib/tema.ts` sobre o
       * `tinta-derivada.ts`: *«o empacotador não move funções, move módulos»* —
       * uma chamada arrasta o ficheiro inteiro. E a resposta é a mesma de lá:
       * mudar o SÍTIO, e não a tese.
       *
       * Quem decide passa a ser a folha, que já é `lazy()` e já importa a régua.
       * Ela pede `/eu`, e se não houver nada a cadastrar fecha-se sozinha — o que
       * cai no `aoFechar` daqui, que é o `reload`. A porta fica a saber uma coisa
       * só: «o aceite passou, mostra o passo 2».
       */
      setFase2(true);
    } catch (e) {
      /* `termos_mudaram`: o tutor publicou outra versão enquanto esta tela
         estava aberta. Rebuscar mostra o texto novo em vez de repetir o erro. */
      if (e instanceof ErroDaApi && e.codigo === 'termos_mudaram') {
        setMarcado(false);
        await buscar(slug);
        setErro(t('termos.atualizadosAgora'));
      } else {
        setErro(e instanceof Error ? e.message : t('termos.naoDeuParaRegistrar'));
      }
    } finally {
      setEnviando(false);
    }
  }

  const tema = dados.tema ?? temaSemeado(slug);

  return (
    /*
     * A ESPERA É A PRÓPRIA CORTINA, e não uma frase.
     *
     * O que está por baixo já levou 403 do servidor em todas as rotas daquela
     * área: deixar aparecer o que quer que seja enquanto o pedaço chega seria
     * mostrar telas de erro por uma fração de segundo. Uma cortina no fundo da
     * área é o mesmo que a folha vai desenhar por cima — nada pisca, nada salta.
     */
    <Suspense fallback={<div className="fixed inset-0 z-50" style={variaveisDoTema(tema)} />}>
      {fase2 ? (
        /*
         * ── FASE 2: A CORTINA FICA, A FOLHA É QUE TROCA ──────────────────────
         *
         * A mesma cortina opaca dos termos, pela mesma razão: o que está por
         * baixo ainda tem os 403 na tela, e ela só sai no `reload`. O que muda é
         * o que está desenhado por cima dela — e é isso que faz os dois passos
         * parecerem um wizard em vez de duas telas sem relação.
         *
         * `aoFechar` é quem recarrega agora, nos dois desfechos (gravou o `@` ou
         * disse «agora não»): o `reload` não desapareceu, mudou de instante. Sem
         * ele a pessoa ficava a olhar para a cortina, com a área por baixo em
         * erro e nada a acontecer.
         */
        <div className="fixed inset-0 z-50" style={variaveisDoTema(tema)}>
          <FolhaDoConvite
            tema={tema}
            /* `wizard`: a folha pede `/eu`, decide se há o que cadastrar, e
               fecha-se sozinha quando não há. O wizard também não é um dos cinco
               convites — ver `contaComoConvite` na folha. */
            wizard
            centradoSempre
            aoFechar={() => window.location.reload()}
          />
        </div>
      ) : (
        <FolhaDosTermos
          termos={termos}
          primeiraVez={primeiraVez}
          tema={tema}
          marcado={marcado}
          aoMarcar={setMarcado}
          enviando={enviando}
          erro={erro}
          aceitar={() => void aceitar()}
        />
      )}
    </Suspense>
  );
}

/*
 * A folha do convite, pelo MESMO `lazy()` que `PortaoDoInstagram` usa.
 *
 * O empacotador reconhece o especificador e os dois portões partilham um pedaço
 * só — quem aceita os termos baixa-o aqui, quem já os tinha aceitado baixa-o lá,
 * e a porta do app não paga por nenhum dos dois.
 */
const FolhaDoConvite = telaComDicionario(() => import('./ConviteDoInstagram.js'));

/*
 * ── A FOLHA CHEGA SOB DEMANDA, E O `EDT-12` FOI QUEM O EXIGIU ───────────────
 *
 * Na primeira versão desta leva o documento inteiro vinha por cima, e a cerca
 * do pedaço de entrada ficou vermelha por 520 B: **335 520 B contra o teto de
 * 335 000**. Ela tinha razão — o teto já tinha subido três vezes no mesmo dia, e
 * está escrito lá que a quarta resposta não é subir, é *«medir o que está na
 * porta e tirar o que não precisa de estar»*.
 *
 * Esta tela é vista **uma vez na vida do aluno naquela área**. O que fica por
 * cima é a decisão (o endereço, o pedido, o `null` para quem já aceitou); o que
 * desce pelo fio, no instante em que há termos por aceitar, é o documento.
 */
const FolhaDosTermos = telaComDicionario(() => import('./FolhaDosTermos.js'));
